import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAsync, useToast } from '../../lib/store';
import type { CollectionCard, WorldBossFighter } from '../../lib/types';
import { CollectionCardTile } from '../collection/CollectionCard';
import { Button, EmptyState, LoadingGrid, Modal, TypeChip } from '../ui';
import { ALL_TYPES, formatNumber, STAT_LABELS, titleCase } from '../../lib/format';

/**
 * Choosing the one Pokemon that represents you — design PART 2.
 *
 * **One choice, and the page has to make it feel like one.** The whole reason
 * this system asks for a single fighter is to make somebody look through their
 * collection and ask "what is the best Pokemon I own for *this*". So the picker
 * is not a dropdown: it is the Collection grid, with the boss's typing on
 * screen while you scroll it, and a confirm step that shows the Pokemon's real
 * stats before anything is locked.
 *
 * **Paged and sorted in the database**, like the Nursery picker and for the
 * same reason — a breeder's collection does not fit in a browser, and sorting
 * after the fetch is only sorting the page you already have. The cards are the
 * ordinary `CollectionCard`, so a fighter is scanned with the same eyes as
 * everything else in the game.
 */

type Sort = 'level' | 'iv' | 'name' | 'recent' | 'dex';

interface PickerPage {
  pokemon: CollectionCard[];
  total: number;
  page: number;
  pages: number;
}

export function FighterPicker({
  bossName,
  bossTypes,
  currentId,
  onChosen,
  onClose,
}: {
  bossName: string;
  bossTypes: string[];
  currentId: string | null;
  onChosen: () => void;
  onClose: () => void;
}) {
  const { push } = useToast();
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [sort, setSort] = useState<Sort>('level');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string | null>(currentId);
  const [busy, setBusy] = useState(false);

  const query = new URLSearchParams({ sort, page: String(page), pageSize: '24' });
  if (search.trim()) query.set('search', search.trim());
  if (type) query.set('type', type);

  const list = useAsync<PickerPage>(
    () => api.get(`/world-boss/fighters?${query.toString()}`),
    [sort, page, search, type],
  );

  // The confirm panel's numbers come from the server rather than from the card,
  // because the card carries a genes *summary* and this is the screen where
  // somebody compares two Rhydon. Fetched on selection, not on hover.
  const detail = useAsync<{ fighter: WorldBossFighter }>(
    () => (selected ? api.get(`/world-boss/fighters/${selected}`) : Promise.resolve({ fighter: null as never })),
    [selected],
  );

  useEffect(() => {
    setPage(1);
  }, [search, type, sort]);

  async function confirm() {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post('/world-boss/fighter', { pokemonId: selected });
      push('success', 'Fighter confirmed.');
      onChosen();
      onClose();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not confirm that fighter.');
    } finally {
      setBusy(false);
    }
  }

  const fighter = detail.data?.fighter ?? null;

  return (
    <Modal onClose={onClose} wide labelledBy="picker-title">
      <div className="stack wb-picker">
        <div>
          <h2 id="picker-title" className="page-head__title">
            Choose your fighter
          </h2>
          {/* The boss's typing, on screen while the grid is scrolled. This is
              the single most useful thing the picker can show — PART 30 wants
              type preparation to be the strategy, and it cannot be if the
              player has to remember what they are fighting. */}
          <p className="tiny muted">
            One Pokémon represents you against {bossName}
            {bossTypes.length > 0 && (
              <>
                {' — '}
                {bossTypes.map((entry) => (
                  <TypeChip key={entry} type={entry} />
                ))}
              </>
            )}
            . You can change your mind freely until your first attack.
          </p>
        </div>

        <div className="row wb-picker__controls">
          <input
            className="input"
            placeholder="Search by name…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <select className="input" value={type} onChange={(event) => setType(event.target.value)}>
            <option value="">Any type</option>
            {ALL_TYPES.map((entry) => (
              <option key={entry} value={entry}>
                {titleCase(entry)}
              </option>
            ))}
          </select>
          <select
            className="input"
            value={sort}
            onChange={(event) => setSort(event.target.value as Sort)}
          >
            <option value="level">Highest level</option>
            <option value="iv">Best genetics</option>
            <option value="name">Name</option>
            <option value="dex">Pokédex order</option>
            <option value="recent">Newest</option>
          </select>
        </div>

        {list.loading && !list.data ? (
          <LoadingGrid count={8} />
        ) : (list.data?.pokemon.length ?? 0) === 0 ? (
          <EmptyState title="Nothing matches" art="○">
            Try a different search, or clear the type filter.
          </EmptyState>
        ) : (
          // `selectMode` rather than a link: a card that navigated to the
          // Pokemon's page mid-choice would throw the selection away, and the
          // tile already knows how to be a button. `compact` because a picker is
          // scanned rather than studied — the confirm panel below is where the
          // detail lives.
          <div className="collection-grid collection-grid--compact wb-picker__grid">
            {list.data!.pokemon.map((card) => (
              <CollectionCardTile
                key={card.id}
                card={card}
                compact
                selectMode
                selected={selected === card.id}
                onToggleSelect={(picked) => setSelected(picked.id)}
                onToggleFavorite={() => {}}
              />
            ))}
          </div>
        )}

        {(list.data?.pages ?? 1) > 1 && (
          <div className="row row--between">
            <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <span className="tiny muted">
              Page {list.data!.page} of {list.data!.pages} · {formatNumber(list.data!.total)} Pokémon
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={page >= (list.data?.pages ?? 1)}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        )}

        {/* The confirm step. Exact stats here, unlike the boss's banded ones:
            this is your own Pokemon and the whole point of the screen is
            comparing two of them. */}
        <div className="pxframe wb-picker__confirm">
          {fighter ? (
            <>
              <div className="row" style={{ gap: 'var(--gap)' }}>
                <img className="pixel" src={fighter.sprite ?? ''} alt="" width={72} height={72} />
                <div className="stack-sm" style={{ flex: 1 }}>
                  <strong>
                    {fighter.isShiny && <span style={{ color: 'var(--gold-dark)' }}>✦ </span>}
                    {fighter.name}
                  </strong>
                  <div className="tiny muted">
                    Lv {fighter.level}
                    {fighter.natureName && ` · ${fighter.natureName}`} · Potential {fighter.ivPercent}%
                  </div>
                  <div className="row" style={{ gap: 4 }}>
                    {fighter.types.map((entry) => (
                      <TypeChip key={entry} type={entry} />
                    ))}
                  </div>
                </div>
              </div>

              <dl className="wb-picker__stats">
                {(['hp', 'attack', 'defense', 'specialAttack', 'specialDefense', 'speed'] as const).map(
                  (key) => (
                    <div key={key}>
                      <dt>{STAT_LABELS[key] ?? key}</dt>
                      <dd className="mono">{fighter.stats[key]}</dd>
                    </div>
                  ),
                )}
              </dl>

              <div className="tiny muted">
                Moves: {fighter.moves.map((move) => move.name).join(', ')}
              </div>

              <Button onClick={confirm} disabled={busy}>
                {busy ? 'Confirming…' : 'Confirm fighter'}
              </Button>
            </>
          ) : (
            <p className="tiny muted">Pick a Pokémon above to see its stats and confirm.</p>
          )}
        </div>
      </div>
    </Modal>
  );
}
